import React, { useState, useEffect, useContext, useRef } from "react";
import { UserInfo, AvailableTags, VisualCache } from "../helper/Context";
import {
  Button,
  Spinner,
  Breadcrumb,
  Form,
  Row,
  Col,
  Tabs,
  Tab,
  Image,
  OverlayTrigger,
  Tooltip,
  Modal,
  Dropdown
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useInRouterContext, useRoutes, useSearchParams } from "react-router-dom";
import {
  faMagnifyingGlass,
  faArrowUp,
  faFolderPlus,
  faFolderClosed,
  faFolderOpen,
  faTruckMedical,
  faDesktop,
  faTrash,
  faHeart
} from "@fortawesome/free-solid-svg-icons";
import { faTrashCan, faImages } from "@fortawesome/free-regular-svg-icons";
import { Link } from "react-router-dom";
import {
  APIGetVisualizations,
  APIGetOrgVisuals,
  APIEditVisualizations,
  APICreateVisFolder,
  APIEditVisualFolder,
  APIDeleteVisualFolder,
} from "../helper/APIFunctions";
import CarouselVisCard from "../components/CarouselVisCard";
import CarouselPresentationCard from "../components/CarouselPresentationCard";
import CarouselExplainerCard from "../components/CarouselExplainerCard";
import {
  getVisualLimit,
  canSortVisuals,
  getPresentationLimit,
} from "../helper/BusinessLogic";
import { shallowCopy, buildFolderTree } from "../helper/Utils";
import { filter } from "jszip";
import LoadingOverlay from "../components/LoadingOverlay";
import "./ProfilePage.css";
import "./DashboardPage.css";

const RESOURCEFILTERS = [
  { name: "All visuals", type: "all", owner: "all" },
  { name: "Standalone visuals", type: "visual", owner: "all" },
  { name: "Presentations and Scrollytelling", type: "presentation", owner: "all" },
  { name: "Quizzes", type: "quiz", owner: "all" },
  { name: "Explainer videos", type: "explainer", owner: "all" },
  { name: "Team resources", type: "all", owner: "team" },
];

const isQuizPresentation = (pres) => !!(
  pres.is_quiz ||
  pres.metadata?.is_quiz ||
  pres.slides?.[0]?.type === 'quiz'
);

// Helper function to build a hierarchical tree from a flat list of folders


// Helper function to recursively render dropdown items for folders
const RenderFolderItems = ({ folders, onSelect, selectedFolder, level = 0 }) => {
  const prefix = "\u00A0\u00A0\u00A0\u00A0".repeat(level);
  return folders.map(folder => (
    <React.Fragment key={folder.id}>
      <Dropdown.Item onClick={() => onSelect(folder.id)}>
        {prefix}{folder.title}
      </Dropdown.Item>
      {folder.children && folder.children.length > 0 && (
        <RenderFolderItems folders={folder.children} onSelect={onSelect} selectedFolder={selectedFolder} level={level + 1} />
      )}
    </React.Fragment>
  ));
};

const CardCollection = ({ resources, handlePresDelete, handlePresEdit, handleVisDelete, handleExplainerDelete, handleExplainerEdit, handleVisEdit, folders, orgFolders }) => {
  const getFolderAncestorIdString = (folderId, isOrg) => {
    let currentId = folderId;
    let result = "";
    while (currentId) {
      const folder = isOrg ? orgFolders.find(f => f.id === currentId) : folders.find(f => f.id === currentId);
      if (!folder) break;
      result = folder.id + ";" + result;
      currentId = folder.parent;
    }
    return result;
  };
  const allFolders = [...folders, ...orgFolders];
  const folderTrees = { self: buildFolderTree(folders), team: buildFolderTree(orgFolders) };
  return (
    <Row className="gy-4">
      {resources.map((resource) => (
        <Col key={`${resource}-${resource.id}`} xs={12} sm={6} md={4} lg={3} className={`item-card ${resource.topLevelType}-card`}
          data-id={resource.id} data-type={resource.topLevelType} data-owner={resource.owner}
          data-favorite={resource.favorite ? "true" : ""}
          data-title={resource.title?.toLowerCase() || ""}
          data-description={resource.description?.toLowerCase() || ""} data-created={resource.created_timestamp || resource.created_at}
          data-folderids={getFolderAncestorIdString(resource.folder_id, resource.owner === "team")}>
          {resource.topLevelType === "visual" && (
            <CarouselVisCard
              vis={resource}
              onVisDelete={() => handleVisDelete(resource.id)}
              onVisEdit={handleVisEdit}
              folders={resource.owner === "team" ? orgFolders : folders}
              folderTree={folderTrees[resource.owner || "self"]}
              folder_name={allFolders.find(f => f.id === resource.folder_id)?.title}
              isOrg={resource.owner === "team"}
            />
          )}
          {(resource.topLevelType === "presentation" || resource.topLevelType === "quiz") && (
            <CarouselPresentationCard
              presentation={resource}
              onPresDelete={() => handlePresDelete(resource.id)}
              onPresEdit={handlePresEdit}
              folders={resource.owner === "team" ? orgFolders : folders}
              folderTree={folderTrees[resource.owner || "self"]}
              isOrg={resource.owner === "team"} />
          )}
          {resource.topLevelType === "explainer" && (
            <CarouselExplainerCard
              explainer={resource}
              onExplainerDelete={(explainerId) => {handleExplainerDelete(explainerId)}}
              onExplainerEdit={(explainer)=>{handleExplainerEdit(resource.id, explainer)}} //TODO: Implement explainer edit
              folders={resource.owner === "team" ? orgFolders : folders}
              folderTree={folderTrees[resource.owner || "self"]}
              isOrg={resource.owner === "team"}
            />
          )}
        </Col>
      ))}
    </Row>
  );
};

const AddEditFolderModal = ({ show, onHide, oldFolder = {}, onFolderAddEdit, editOrgVisuals = false, folders = [], orgFolders = [] }) => {
  const allFolders = [...folders, ...orgFolders];
  const folderTrees = { self: buildFolderTree(folders), team: buildFolderTree(orgFolders) };
  const [selectedParentFolder, setSelectedParentFolder] = useState(oldFolder.parent || '');
  const [folderName, setFolderName] = useState(oldFolder.title || 'New Category');
  const [folderOwner, setFolderOwner] = useState(oldFolder.owner || 'self');
  const [folderAPIRequest, setFolderAPIRequest] = useState(null);
  const getFolderNameForCurrentView = (folderId) => {
    if (!folderId) return 'None';
    const folder = allFolders.find(f => f.id === folderId);
    return folder ? folder.title : 'None';
  };
  const hideModal = () => {
    setFolderAPIRequest(null);
    onHide();
  }
  useEffect(() => {
    setSelectedParentFolder(oldFolder.parent || '');
    setFolderName(oldFolder.title || 'New Category');
    setFolderOwner(oldFolder.owner || 'self');
  }, [oldFolder]);
  const addFolder = () => {
    setFolderAPIRequest({ status: "pending" });
    APICreateVisFolder(
      (newFolder) => {
        editFolder(newFolder.id);
      },
      (err) => {
        setFolderAPIRequest({ status: "error", error: err });
      },
      folderOwner === "team"
    );
  };
  const editFolder = (folderId) => {
    setFolderAPIRequest({ status: "pending" });
    const folderProps = { title: folderName };
    if (selectedParentFolder && selectedParentFolder.length) {
      folderProps.parent = selectedParentFolder;
    }
    APIEditVisualFolder(folderId,
      folderProps,
      () => {
        onFolderAddEdit({ id: folderId, parent: folderProps.parent, title: folderProps.title, owner: folderOwner });
        hideModal();
      },
      (err) => {
        setFolderAPIRequest({ status: "error", error: err });
      },
      folderOwner === "team"
    )
  }
  useEffect(() => {
    if (!selectedParentFolder || selectedParentFolder.length === 0) return;
    const curFolder = allFolders.find(f => f.id === selectedParentFolder);
    if (curFolder.owner !== folderOwner) {
      setSelectedParentFolder('');
    }
  }, [folderOwner]);

  return (
    <Modal show={show} onHide={hideModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>{oldFolder.id ? 'Edit Category' : 'Add Category'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="folderName" className="mb-3" as={Row}>
          <Col xs={4}>
            <Form.Label>Category Name</Form.Label>
          </Col>
          <Col xs={8}>
            <Form.Control type="text" placeholder="Enter category name" value={folderName} onChange={(e) => setFolderName(e.target.value)} />
          </Col>
        </Form.Group>

        {!oldFolder.id && editOrgVisuals && (
          <Form.Group controlId="folderOwner" className="mb-3" as={Row}>
            <Col xs={4}>
              <Form.Label>Category Owner</Form.Label>
            </Col>
            <Col xs={8}>
              <Dropdown>
                <Dropdown.Toggle variant="light" id="folder-filter-dropdown">
                  {folderOwner === "team" ? "Team" : "Personal"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setFolderOwner('self')} active={folderOwner === 'self'}>Personal</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFolderOwner('team')} active={folderOwner === 'team'}>Team</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Form.Group>
        )}

        <Form.Group controlId="folderParent" className="mb-3" as={Row}>
          <Col xs={4}>
            <Form.Label>Parent Category</Form.Label>
          </Col>
          <Col xs={8}>
            <Dropdown>
              <Dropdown.Toggle variant="light" id="folder-filter-dropdown">
                {getFolderNameForCurrentView(selectedParentFolder)}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSelectedParentFolder('')} active={selectedParentFolder === ''}>None</Dropdown.Item>
                <Dropdown.Divider />

                <RenderFolderItems folders={folderTrees[folderOwner]} onSelect={setSelectedParentFolder} selectedFolder={selectedParentFolder} />

              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Form.Group>
        <Button variant="primary" onClick={oldFolder.id ? editFolder : addFolder} disabled={folderAPIRequest && folderAPIRequest.status === "pending"}>
          {!folderAPIRequest || folderAPIRequest.status !== "pending" ? (oldFolder.id ? 'Save' : 'Create') : 'Saving...'}
        </Button>
        {folderAPIRequest && folderAPIRequest.status === "error" && <div className="text-danger">{folderAPIRequest.error}</div>}
        <Button variant="secondary" onClick={hideModal} disabled={folderAPIRequest && folderAPIRequest.status === "pending"}>Cancel</Button>
      </Modal.Body>
    </Modal>
  );
};

const CardFilter = ({ folders, orgFolders, onFolderAddEdit, onFolderDelete, setError, editOrgVisuals = false }) => {
  const [activeFilter, setActiveFilter] = useState(RESOURCEFILTERS[0].name);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [initalAddEditFolder, setInitalAddEditFolder] = useState(null);
  // const handleFolderAdd = (isOrg) => {
  //   APICreateVisFolder(
  //     (newFolder) => {
  //       onFolderAdd(newFolder, isOrg);
  //       setSelectedFolder(newFolder.id);
  //     },
  //     (err) => {
//
  //       setError("Could not create a new folder. Please try again.");
  //     },
  //     isOrg
  //   );
  // };

  const allFolders = [...folders, ...orgFolders];

  const handleFolderDelete = () => {
    if (selectedFolder === 'all' || selectedFolder === null) return;
    const folder = allFolders.find(f => f.id === selectedFolder);
    if (!folder) return;
    APIDeleteVisualFolder(
      { id: selectedFolder },
      () => {
        onFolderDelete(selectedFolder, folder.owner === "team");
        setSelectedFolder('all');
        setShowDeleteFolderModal(false);
      },
      (err) => {
        setError("Could not delete the folder. Please try again.");
        setShowDeleteFolderModal(false);
      },
      folder.owner === "team"
    );
  };

  // Filter the cards based on the current filter and selected folder using the data- attributes and bypassing the react render cycle
  useEffect(() => {
    const cards = document.querySelectorAll('.item-card');
    const curFilter = RESOURCEFILTERS.find(f => f.name === activeFilter);
    let showingSomething = false;
    const showCard = (card) => {
      if (curFilter.type !== 'all' && curFilter.type !== card.dataset.type) {
        return false;
      }
      if (curFilter.owner !== 'all' && curFilter.owner !== card.dataset.owner) {
        return false;
      }
      if (showFavoritesOnly && !card.dataset.favorite) {
        return false;
      }
      if (selectedFolder !== 'all' && selectedFolder !== null && !card.dataset.folderids.includes(selectedFolder)) {
        return false;
      }
      if (searchTerm && !card.dataset.title.includes(searchTerm) && !card.dataset.description.includes(searchTerm)) {
        return false;
      }
      return true;
    }
    cards.forEach(card => {
      card.style.display = showCard(card) ? 'block' : 'none';
      showingSomething = showingSomething || showCard(card);
    });
    const noItemsFound = document.getElementById('no-items-found');
    if (noItemsFound) {
      noItemsFound.style.display = showingSomething ? 'none' : 'block';
    }
  }, [activeFilter, selectedFolder, searchTerm, showFavoritesOnly]);

  // If the selected folder is not of the current filter's owner, set it to 'all'
  useEffect(() => {
    if (selectedFolder === 'all' || selectedFolder === null) return;
    const curFilter = RESOURCEFILTERS.find(f => f.name === activeFilter);
    const curFolder = allFolders.find(f => f.id === selectedFolder);
    if (curFilter.owner !== 'all' && curFolder.owner !== curFilter.owner) {
      setSelectedFolder('all');
    }
  }, [activeFilter]);


  const getFolderNameForCurrentView = (folderId) => {
    if (!folderId) return 'Root';
    const folder = allFolders.find(f => f.id === folderId);
    return folder ? folder.title : 'Root';
  };
  const getFolderOwner = (folderId) => {
    const folder = allFolders.find(f => f.id === folderId);
    return folder ? folder.owner : "self";
  };
  const currentFilter = RESOURCEFILTERS.find(f => f.name === activeFilter);
  let folderTrees = [];
  if (currentFilter.owner === 'all') {
    folderTrees = [buildFolderTree(folders), buildFolderTree(orgFolders)];
  } else if (currentFilter.owner === 'self') {
    folderTrees = [buildFolderTree(folders)];
  } else {
    folderTrees = [buildFolderTree(orgFolders)];
  }

  return (<>

    <div className="d-flex justify-content-center align-items-center mb-4">
      <Form.Control
        type="text"
        placeholder="Search my library..."
        onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
        value={searchTerm}
        className="search-bar"
        style={{ maxWidth: '400px' }}
      />
    </div>

    <Row className="justify-content-center align-items-center mb-5">
      <Col xs="auto">
        <Dropdown>
          <Dropdown.Toggle variant="outline-light" id="visual-type-filter-dropdown" className="filter-dropdown">
            {activeFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu>{RESOURCEFILTERS.map(filter => (
            <Dropdown.Item key={filter.name} onClick={() => setActiveFilter(filter.name)} active={activeFilter === filter.name}>{filter.name}</Dropdown.Item>
          ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
      <Col xs="auto">
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-favorites">{showFavoritesOnly ? 'Unfilter' : 'Filter for favorites'}</Tooltip>}
          delay={{ show: 1500, hide: 250 }}
        >
          <Button variant={showFavoritesOnly ? "primary" : "outline-light"} onClick={() => setShowFavoritesOnly(!showFavoritesOnly)} className="icon-button">
            <FontAwesomeIcon icon={faHeart} />
          </Button>
        </OverlayTrigger>
      </Col>
    </Row>

    <Row className="justify-content-center align-items-center mb-5">
      <Col xs="auto">
        <Dropdown>
          <Dropdown.Toggle variant="outline-light" id="category-filter-dropdown" className="filter-dropdown">
            {selectedFolder === 'all' ? 'All categories' : selectedFolder === null ? 'Uncategorized' : getFolderNameForCurrentView(selectedFolder)}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => setSelectedFolder('all')} active={selectedFolder === 'all'}>All categories</Dropdown.Item>
            <Dropdown.Item onClick={() => setSelectedFolder(null)} active={selectedFolder === null}>Uncategorized</Dropdown.Item>
            {folderTrees.filter(tree => tree.length > 0).map((tree, index) => (
              <React.Fragment key={index}>
                <Dropdown.Divider />
                <RenderFolderItems folders={tree} onSelect={setSelectedFolder} selectedFolder={selectedFolder} />
              </React.Fragment>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
      <Col xs="auto">
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-create-category">Create new category</Tooltip>}
          delay={{ show: 1500, hide: 250 }}
        >
          <Button variant="outline-light" onClick={() => { setInitalAddEditFolder(selectedFolder && selectedFolder !== 'all' ? { owner: editOrgVisuals ? getFolderOwner(selectedFolder) : "self", parent: selectedFolder } : null); setShowAddFolderModal(true); }} className="icon-button">
          <FontAwesomeIcon icon={faFolderPlus} />
        </Button>
        </OverlayTrigger>
      </Col>
      <Col xs="auto">
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-delete-category">Delete category</Tooltip>}
          delay={{ show: 1500, hide: 250 }}
        >
          <span className="d-inline-block">
        <Button variant="outline-danger" disabled={selectedFolder === 'all' ||
          selectedFolder === null ||
          (getFolderOwner(selectedFolder) === "team" && !editOrgVisuals)}
              onClick={() => setShowDeleteFolderModal(true)} className="icon-button" style={{ pointerEvents: (selectedFolder === 'all' || selectedFolder === null || (getFolderOwner(selectedFolder) === "team" && !editOrgVisuals)) ? 'none' : 'auto' }}>
          <FontAwesomeIcon icon={faTrash} />
        </Button>
          </span>
        </OverlayTrigger>
      </Col>
    </Row>

    <Modal show={showDeleteFolderModal} onHide={() => setShowDeleteFolderModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Deletion</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Are you sure you want to delete the folder "{getFolderNameForCurrentView(selectedFolder)}"?
        Visuals inside this folder will not be deleted.
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowDeleteFolderModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleFolderDelete}>
          Delete Folder
        </Button>
      </Modal.Footer>
    </Modal>
    <AddEditFolderModal
      show={showAddFolderModal}
      onHide={() => setShowAddFolderModal(false)}
      addEditFolder={initalAddEditFolder}
      onFolderAddEdit={(f) => {
        setInitalAddEditFolder(f.id || "all");
        if (currentFilter.owner !== "all" && f.owner !== currentFilter.owner) {
          setActiveFilter(RESOURCEFILTERS[0].name);
        }
        onFolderAddEdit(f);
        setSelectedFolder(f.id || "all");
      }}
      setError={setError}
      oldFolder={initalAddEditFolder || {}}
      editOrgVisuals={editOrgVisuals}
      folders={folders} orgFolders={orgFolders} />

  </>
  );
}



const ProfilePage = (props) => {
  const { userInfo } = useContext(UserInfo);
  const { visCache } = useContext(VisualCache);
  const [loading, setLoading] = useState(true);
  const [visuals, setVisuals] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [videos, setVideos] = useState([]);
  const [folders, setFolders] = useState([]);
  const [explainers, setExplainers] = useState([]);
  const [orgVisuals, setOrgVisuals] = useState([]);
  const [orgPresentations, setOrgPresentations] = useState([]);
  const [orgVideos, setOrgVideos] = useState([]);
  const [orgFolders, setOrgFolders] = useState([]);
  const [error, setError] = useState(null);
  const [showLoading, setShowLoading] = useState(true);

  // Helper to normalize slides, same as in CreatePresentationPage
  function normalizePresentation(pres) {
    const normalizedPres = {
      ...pres,
      slides: (pres.slides || []).map(slide => {
        let visual = null;
        if (visCache && slide.visual_id && visCache[slide.visual_id]) {
          visual = visCache[slide.visual_id];
        }
        return {
          ...visual, // full visual info first
          ...slide,  // slide-specific overrides (title, description, intro, etc.)
          visualization_url: (slide.visualization_url || slide.visual_url || (visual && visual.visualization_url) || ""),
          thumbnail: (slide.thumbnail || (visual && visual.thumbnail) || ""),
        };
      })
    };
    normalizedPres.thumbnail = pres.thumbnail ||  (normalizedPres?.slides?.length > 0 ? normalizedPres?.slides[0]?.thumbnail : "");
    normalizedPres.topLevelType = isQuizPresentation(normalizedPres) ? "quiz" : "presentation";
    return normalizedPres;
  }

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const personalDataPromise = new Promise((resolve, reject) => {
          APIGetVisualizations(
            (body) => resolve(body),
            (err) => reject(err),
            visCache
          );
        });

        const promises = [personalDataPromise];

        if (userInfo && userInfo?.organization_permissions) {
          const orgDataPromise = new Promise((resolve, reject) => {
            APIGetOrgVisuals(
              (body) => resolve(body),
              (err) => reject(err)
            );
          });
          promises.push(orgDataPromise);
        }

        const results = await Promise.all(promises);

        const personalData = results[0];
        setVisuals((personalData.visuals || []).filter(v => !v.hide_in_library).map(v => {
          v.owner = "self";
          v.topLevelType = "visual";
          return v;
        }));
        setPresentations((personalData.presentations || []).filter(p => !p.hide_in_library).map(p => {
          const pres = normalizePresentation(p);
          if (!pres.thumbnail && pres.slides && pres.slides.length > 0) {
            pres.thumbnail = pres.slides[0].thumbnail;
          }
          pres.owner = "self";
          pres.topLevelType = isQuizPresentation(pres) ? "quiz" : "presentation";
          return pres;
        }));
        setVideos((personalData.videos || []).filter(v => !v.hide_in_library).map(v => {
          v.owner = "self";
          v.topLevelType = "visual";
          v.type = "video";
          return v;
        }));
        setFolders((personalData.folders || []).filter(f => !f.hide_in_library).map(f => {
          f.owner = "self";
          return f;
        }));

        if (results.length > 1) {
          const orgData = results[1];
          setOrgVisuals((orgData.visuals || []).filter(v => !v.hide_in_library).map(v => {
            v.owner = "team";
            v.topLevelType = "visual";
            return v;
          }));
          setOrgPresentations((orgData.presentations || []).filter(p => !p.hide_in_library).map(p => {
            const pres = normalizePresentation(p);
            if (!pres.thumbnail && pres.slides && pres.slides.length > 0) {
              pres.thumbnail = pres.slides[0].thumbnail;
            }
            pres.owner = "team";
            pres.topLevelType = isQuizPresentation(pres) ? "quiz" : "presentation";
            return pres;
          }));
          setOrgVideos((orgData.videos || []).filter(v => !v.hide_in_library).map(v => {
            v.owner = "team";
            v.topLevelType = "visual";
            return v;
          }));
          setOrgFolders((orgData.folders || []).filter(f => !f.hide_in_library).map(f => {
            f.owner = "team";
            return f;
          }));
        }
        setExplainers((personalData.explainers || []).filter(t => !t.hide_in_library).map(t => {
          t.owner = "self";
          t.topLevelType = "explainer";
          return t;
        }));
      } catch (err) {
        setError("Could not load visuals. Please try again later and contact us if the problem persists.");
      } finally {
        setLoading(false);
      }
    };

    if (userInfo) {
      fetchAllData();
    }
  }, [userInfo]);


  const handleVisDelete = (id) => {
    setVisuals(visuals.filter(v => v.id !== id));
    setVideos(videos.filter(v => v.id !== id));
  };

  const handleVisEdit = (editedVis) => {
    setVisuals(visuals.map(v => v.id === editedVis.id ? editedVis : v));
    setVideos(videos.map(v => v.id === editedVis.id ? editedVis : v));
  };

  const handlePresDelete = (id) => {
    setPresentations(presentations.filter(p => p.id !== id));
    setOrgPresentations(orgPresentations.filter(p => p.id !== id));
  };

  const handleExplainerDelete = (id) => {
    setExplainers(prevExplainers => prevExplainers.filter(e => e.id !== id));
  };

  const handleExplainerEdit = (id, editedExplainer) => {
    setExplainers(prevExplainers => prevExplainers.map(e => e.id === id ?Object.assign(e, {title: editedExplainer.title, secondary_text: editedExplainer.secondary_text}) : e));
  };

  const handlePresEdit = (editedPres) => {
    setPresentations(presentations.map(p => p.id === editedPres.id ? normalizePresentation(editedPres) : p));
    setOrgPresentations(orgPresentations.map(p => p.id === editedPres.id ? normalizePresentation(editedPres) : p));
  };
  const getFolderName = (folderId) => {
    if (!folderId) return 'Root';
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.title : 'Root';
  };

  // const isTeamView = activeFilter === 'team';
  // const foldersForDropdown = isTeamView ? orgFolders : folders;

  // const getFolderNameForCurrentView = (folderId) => {
  //   if (!folderId) return 'Root';
  //   const folder = foldersForDropdown.find(f => f.id === folderId);
  //   return folder ? folder.title : 'Root';
  // };



  // if (activeFilter === 'team') {
  //   visualsToFilter = orgVisuals.map(v => ({ ...v, isOrg: true }));
  //   presentationsToFilter = orgPresentations.map(p => ({ ...p, isOrg: true }));
  //   } else {
  //   const allPersonalVisuals = [...visuals, ...videos];
  //   if (activeFilter === 'all') {
  //     visualsToFilter = allPersonalVisuals;
  //     presentationsToFilter = presentations;
  //   } else if (activeFilter === 'visuals') {
  //     visualsToFilter = allPersonalVisuals;
  //   } else if (activeFilter === 'presentations') {
  //     presentationsToFilter = presentations;
  //   } else if (activeFilter === 'favorites') {
  //     visualsToFilter = allPersonalVisuals.filter(v => v.favorite);
  //     presentationsToFilter = presentations.filter(p => p.favorite);
  //   }
  // }

  // const visualsWithFolderName = visualsToFilter.map(vis => ({ ...vis, folder_name: getFolderNameForCurrentView(vis.folder_id) }));

  // const folderIdsToFilter = selectedFolder !== 'all' && selectedFolder !== null 
  //   ? getDescendantFolderIds(selectedFolder, foldersForDropdown) 
  //   : [];

  // const filteredVisuals = visualsWithFolderName
  //   .filter(vis => vis.title.toLowerCase().includes(searchTerm.toLowerCase()))
  //   .filter(vis => {
  //       if (selectedFolder === 'all') return true;
  //       if (selectedFolder === null) return !vis.folder_id;
  //       return folderIdsToFilter.includes(vis.folder_id);
  //   });

  // const filteredPresentations = (selectedFolder !== 'all' && selectedFolder !== null ? [] : presentationsToFilter)
  //   .filter(pres => pres.title.toLowerCase().includes(searchTerm.toLowerCase()));

  // const folderTree = buildFolderTree(foldersForDropdown);

  if (loading) {
    return <LoadingOverlay isLoading={true} message="Loading your library..." />;
  }

  if (error) {
    return <div className="text-center text-danger mt-5">{error}</div>;
  }
  const allResources = [...visuals, ...videos, ...presentations, ...orgVisuals, ...orgPresentations, ...orgVideos, ...explainers].sort(
    (a, b) => parseFloat(b.created_timestamp) - parseFloat(a.created_timestamp)).sort(
      (a, b) => (b.favorite || 0) - (a.favorite || 0));

  return (
    <>
      <UserInfo.Consumer>
        {({ userInfo }) =>
          userInfo.tier === "free" && (
            <h5 className="text-center text-info mb-4">
              Free plan: 10 visuals max, links are live for 10 days.
            </h5>
          )
        }
      </UserInfo.Consumer>
      <div className="profile-page-container">
        <h1 className="gradient-text text-center mb-4">My Library</h1>

        {/* <div className="d-flex justify-content-center align-items-center mb-5">
          <Button variant="link" className={`filter-button ${activeFilter === 'all' ? 'active' : ''}`} onClick={() => setActiveFilter('all')}>All</Button>
          {/* <Button variant="link" className={`filter-button ${activeFilter === 'visuals' ? 'active' : ''}`} onClick={() => setActiveFilter('visuals')}>Visuals</Button> }
          <Button variant="link" className={`filter-button ${activeFilter === 'presentations' ? 'active' : ''}`} onClick={() => setActiveFilter('presentations')}>Presentations</Button>
          <Button variant="link" className={`filter-button ${activeFilter === 'team' ? 'active' : ''}`} onClick={() => setActiveFilter('team')}>Team Visuals</Button>
          <Button variant="link" className={`filter-button ${activeFilter === 'favorites' ? 'active' : ''}`} onClick={() => setActiveFilter('favorites')}>Favorites</Button>
        </div> */}
        <CardFilter folders={folders} orgFolders={orgFolders}
          onFolderAddEdit={(newFolder) => newFolder.owner === "team" ?
            setOrgFolders(prevFolders => [...prevFolders.filter(f => f.id !== newFolder.id), newFolder]) :
            setFolders(prevFolders => [...prevFolders.filter(f => f.id !== newFolder.id), newFolder])}
          onFolderDelete={(folderId, isOrg) => isOrg ? setOrgFolders(prevFolders => prevFolders.filter(f => f.id !== folderId)) : setFolders(prevFolders => prevFolders.filter(f => f.id !== folderId))}
          setError={setError}
          editOrgVisuals={userInfo?.organization_permissions?.edit_visuals}
        />
        <CardCollection resources={allResources}
          handlePresDelete={handlePresDelete}
          handlePresEdit={handlePresEdit}
          handleVisDelete={handleVisDelete}
          handleVisEdit={handleVisEdit}
          handleExplainerDelete={handleExplainerDelete}
          handleExplainerEdit={handleExplainerEdit}
          folders={folders}
          orgFolders={orgFolders}
          editOrgVisuals={userInfo?.organization_permissions?.edit_visuals}
          deleteOrgVisuals={userInfo?.organization_permissions?.delete_visuals}
        />
        {/* <Row className="gy-4">
          {visuals.map((vis) => (
            <Col key={`vis-${vis.id}`} xs={12} sm={6} md={4} lg={3} className="item-card visual-card" data-id={vis.id} data-type={vis.topLevelType} data-owner={vis.owner} data-favorite={vis.favorite} data-title={vis.title?.toLowerCase() || ""} data-description={vis.description?.toLowerCase() || ""} data-created={vis.created_at}>
              <CarouselVisCard
                vis={vis}
                onVisDelete={() => handleVisDelete(vis.id)}
                onVisEdit={handleVisEdit}
                folders={folders}
                isOrg={vis.owner === "team"}
              />
            </Col>
          ))}
                    {videos.map((vis) => (
            <Col key={`vis-${vis.id}`} xs={12} sm={6} md={4} lg={3} className="item-card visual-card" data-id={vis.id} data-type={vis.topLevelType} data-owner={vis.owner} data-favorite={vis.favorite} data-title={vis.title?.toLowerCase() || ""} data-description={vis.description?.toLowerCase() || ""} data-created={vis.created_at}>
              <CarouselVisCard
                vis={vis}
                onVisDelete={() => handleVisDelete(vis.id)}
                onVisEdit={handleVisEdit}
                folders={folders}
                isOrg={vis.owner === "team"}
              />
            </Col>
          ))}
          {presentations.map((pres) => (
            <Col key={`pres-${pres.id}`} xs={12} sm={6} md={4} lg={3} className="item-card presentation-card" data-id={pres.id} data-type={pres.topLevelType} data-owner={pres.owner} data-favorite={pres.favorite} data-title={pres.title?.toLowerCase() || ""} data-description={pres.description?.toLowerCase() || ""} data-created={pres.created_at}>
              <CarouselPresentationCard 
                presentation={pres} 
                onPresDelete={() => handlePresDelete(pres.id)}
                isOrg={pres.owner === "team"}
              />
            </Col>
          ))}
          {orgVisuals.map((vis) => (
            <Col key={`vis-${vis.id}`} xs={12} sm={6} md={4} lg={3} className="item-card visual-card" data-id={vis.id} data-type={vis.topLevelType} data-owner={vis.owner} data-favorite={vis.favorite} data-title={vis.title?.toLowerCase() || ""} data-description={vis.description?.toLowerCase() || ""} data-created={vis.created_at}>
              <CarouselVisCard 
                vis={vis} 
                onVisDelete ={() => handleVisDelete(vis.id)}
                onVisEdit={handleVisEdit}
                folders={orgFolders}
                isOrg={vis.owner === "team"}
              />
            </Col>
          ))}
          {orgVideos.map((vis) => (
            <Col key={`vis-${vis.id}`} xs={12} sm={6} md={4} lg={3} className="item-card visual-card" data-id={vis.id} data-type={vis.topLevelType} data-owner={vis.owner} data-favorite={vis.favorite} data-title={vis.title?.toLowerCase() || ""} data-description={vis.description?.toLowerCase() || ""} data-created={vis.created_at}>
              <CarouselVisCard
                vis={vis}
                onVisDelete={() => handleVisDelete(vis.id)}
                onVisEdit={handleVisEdit}
                folders={orgFolders}
                isOrg={vis.owner === "team"}
              />
            </Col>
          ))}
          {orgPresentations.map((pres) => (
            <Col key={`pres-${pres.id}`} xs={12} sm={6} md={4} lg={3} className="item-card presentation-card" data-id={pres.id} data-type={pres.topLevelType} data-owner={pres.owner} data-favorite={pres.favorite} data-title={pres.title?.toLowerCase() || ""} data-description={pres.description?.toLowerCase() || ""} data-created={pres.created_at} data-updated={pres.updated_at}>
              <CarouselPresentationCard 
                presentation={pres} 
                onPresDelete={() => handlePresDelete(pres.id)}
                onPresEdit={handlePresEdit}
                isOrg={pres.isOrg}
              />
            </Col>
          ))}
        </Row> */}
        {!loading && !error && !allResources.length && (
          <div className="text-center text-light mt-5" id="no-visuals">
            <h4>Your library is empty! Create some visuals or presentations to get started.</h4>
          </div>
        )}
        {!loading && [visuals, videos, presentations, orgPresentations, orgVideos].some(e => e.length > 0) && (
          <div className="text-center text-light mt-5" id="no-items-found" style={{ display: 'none' }}>
            <h4>No items match your search.</h4>
          </div>
        )}

      </div>
    </>
  );
};

export default ProfilePage;
